<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-warning header-enhanced">
      <!-- Influencer Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Influencers"
            type="gradient-warning"
            :sub-title="stats.totalInfluencers ? stats.totalInfluencers.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">↗ {{ stats.influencerGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Active Campaigns"
            type="gradient-success"
            :sub-title="stats.activeCampaigns ? stats.activeCampaigns.toLocaleString() : 'Loading...'"
            icon="ni ni-trophy"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Live</span>
              <span class="text-nowrap">Currently running</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Reach"
            type="gradient-info"
            :sub-title="stats.totalReach ? (stats.totalReach / 1000000).toFixed(1) + 'M' : 'Loading...'"
            icon="ni ni-world"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">👥 Audience</span>
              <span class="text-nowrap">Combined reach</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Avg. Engagement"
            type="gradient-purple"
            :sub-title="stats.avgEngagement ? stats.avgEngagement + '%' : 'Loading...'"
            icon="ni ni-heart-2"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">💝 Rate</span>
              <span class="text-nowrap">Engagement rate</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-single-02 mr-2"></i>Influencer Management
          </h3>
          <p class="text-white-50 mb-0">Manage influencer partnerships and campaigns</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button variant="light" size="sm" class="shadow-sm">
            <i class="ni ni-fat-add mr-1"></i>Add Influencer
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <b-card no-body class="influencer-card shadow">
        <b-card-header class="bg-gradient-warning border-0">
          <b-row align-v="center">
            <b-col>
              <h5 class="mb-0 text-white">
                <i class="ni ni-collection mr-2"></i>Influencer Dashboard
              </h5>
              <p class="text-white-50 mb-0 small">Campaign management and influencer insights</p>
            </b-col>
          </b-row>
        </b-card-header>

        <div class="tabs-container">
          <b-tabs content-class="mt-3 px-4 pb-4" fill
                  v-model="tabIndex"
                  nav-class="custom-tabs">
            <b-tab :title-link-class="linkClass(0)"
                   title="Influencer List"
                   active>
              <template #title>
                <i class="ni ni-single-02 mr-2"></i>Influencer List
              </template>
              <influencer-list />
            </b-tab>
            <b-tab :title-link-class="linkClass(1)"
                   title="Schedule"
                   lazy>
              <template #title>
                <i class="ni ni-calendar-grid-58 mr-2"></i>Schedule
              </template>
              <schedule />
            </b-tab>
          </b-tabs>
        </div>
      </b-card>
    </div>
    
    <!-- Dialog -->
    <v-dialog/>

    <!-- Modal -->
    <modals-container />
  </div>
</template>

<script>
  const InfluencerList = () => import('./InfluencerList.vue');
  const Schedule = () => import('./Schedule.vue');

  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  export default {
    name : 'Influencers',
    data() {
      return {
        tabIndex : 0,
        stats: {
          totalInfluencers: null,
          activeCampaigns: null,
          totalReach: null,
          avgEngagement: null,
          influencerGrowth: 0,
        },
      }
    },
    methods : {
      linkClass(idx) {
        if (this.tabIndex === idx)
          return [ 'bg-white', 'text-warning', 'font-weight-bold', 'custom-tab', 'active' ];

        return [ 'bg-transparent', 'text-white', 'custom-tab' ];
      },
      initializeStats() {
        // Simulated influencer statistics - replace with actual API calls
        this.stats.totalInfluencers = 247;
        this.stats.activeCampaigns = 18;
        this.stats.totalReach = 25000000; // 25M
        this.stats.avgEngagement = 4.2;
        this.stats.influencerGrowth = 15.3;
      },
    },
    components : {
      InfluencerList,
      Schedule,
      baseHeader,
      StatsCard
    },
    mounted() {
      this.initializeStats();
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/influencers/influencers";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(245, 166, 35, 0.9) 0%, rgba(230, 126, 34, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 193, 7, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 193, 7, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Card and Tabs */
.influencer-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.influencer-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
}

.tabs-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0 0 12px 12px;
}

/* Custom Tabs Styling */
.custom-tabs {
  background: rgba(245, 166, 35, 0.1);
  border-bottom: 2px solid rgba(245, 166, 35, 0.3);
  padding: 0 20px;
}

.custom-tab {
  border: none !important;
  border-radius: 8px 8px 0 0 !important;
  margin: 0 2px;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
  position: relative;
}

.custom-tab:hover {
  background: rgba(245, 166, 35, 0.2) !important;
  color: #f5a623 !important;
}

.custom-tab.active {
  background: #fff !important;
  color: #f5a623 !important;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.custom-tab i {
  margin-right: 8px;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card-hover {
  animation: fadeInUp 0.6s ease-out;
}

.influencer-card {
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .influencer-card:hover {
    transform: none;
  }

  .custom-tab {
    padding: 10px 15px !important;
    font-size: 14px;
  }

  .custom-tab i {
    margin-right: 5px;
  }
}

table {
  z-index: 99;
  position: relative;
}
</style>